import { useEffect } from "react";
import { supabase } from "./lib/supabase";
import { calculateQualificationThickness } from "./w47-qualification-rules";

const replacements: Array<[string, string]> = [
  ["S · Groove weld with backing", "S Classification"],
  ["T · Open-root groove, plate", "T Classification"],
  ["T · Open-root groove, pipe", "T Classification"],
];

const transferLabel = (value: string | null | undefined) => {
  if (value === "short_circuit") return "Short Circuit";
  if (value === "spray_globular_pulsed") return "Spray / Globular / Pulsed";
  if (value === "dual" || value === "dual_mode") return "Dual Mode";
  return value || "—";
};

export default function QualificationCardCleanup() {
  useEffect(() => {
    let cancelled = false;

    const enrichCard = async (card: Element) => {
      if (card.getAttribute("data-qualification-enriched") === "loading") return;

      const process = card.querySelector(".process")?.textContent?.trim();
      const rows = [...card.querySelectorAll("dl > div")];
      const valueFor = (label: string) => rows.find(row => row.querySelector("dt")?.textContent?.trim().toUpperCase() === label)?.querySelector("dd")?.textContent?.trim();
      const tested = valueFor("TESTED");
      const welderIdText = valueFor("WELDER ID");
      const standard = valueFor("STANDARD");
      const isAnnexJ = standard === "CSA W47.1-J";

      if (!process || !tested || !welderIdText || (process !== "GMAW" && !isAnnexJ)) return;

      card.setAttribute("data-qualification-enriched", "loading");
      const idNumber = welderIdText.replace(/^\S+\s+/, "").trim();

      const { data: qualifications } = await supabase
        .from("qualifications")
        .select("id,process,transfer_mode,qualification_scope,annex_j_test_code,test_thickness_mm,test_gauge,annex_j_tested_thickness_mm,annex_j_tested_gauge,authority_id")
        .eq("process", process)
        .eq("test_date", tested);

      if (cancelled) return;
      const candidates = qualifications || [];
      const authorityIds = candidates.map((q: any) => q.authority_id).filter(Boolean);
      let match: any = candidates.length === 1 ? candidates[0] : null;

      if (authorityIds.length) {
        const { data: authorityRows } = await supabase
          .from("welder_authority_ids")
          .select("id,id_number")
          .in("id", authorityIds);
        if (cancelled) return;
        const matchingAuthority = (authorityRows || []).find((a: any) => a.id_number === idNumber);
        if (matchingAuthority) match = candidates.find((q: any) => q.authority_id === matchingAuthority.id) || match;
      }

      if (!match) {
        card.removeAttribute("data-qualification-enriched");
        return;
      }

      const dl = card.querySelector("dl");
      if (!dl) return;

      if (process === "GMAW" && match.transfer_mode) {
        let transferRow = [...dl.children].find(child => child.querySelector("dt")?.textContent?.trim().toUpperCase() === "TRANSFER MODE") as HTMLElement | undefined;
        if (!transferRow) {
          transferRow = document.createElement("div");
          const dt = document.createElement("dt");
          const dd = document.createElement("dd");
          dt.textContent = "TRANSFER MODE";
          transferRow.append(dt, dd);
          const scopeRow = [...dl.children].find(child => child.querySelector("dt")?.textContent?.trim().toUpperCase() === "QUALIFICATION SCOPE");
          if (scopeRow?.nextSibling) dl.insertBefore(transferRow, scopeRow.nextSibling);
          else dl.appendChild(transferRow);
        }
        const dd = transferRow.querySelector("dd");
        if (dd) dd.textContent = transferLabel(match.transfer_mode);
      }

      if (isAnnexJ && match.qualification_scope === "annex_j") {
        const rawThickness = match.test_thickness_mm ?? match.annex_j_tested_thickness_mm;
        const rawGauge = match.test_gauge ?? match.annex_j_tested_gauge;
        const calculated = calculateQualificationThickness({
          materialDivision: "steel",
          scope: "annex_j",
          annexJCode: match.annex_j_test_code,
          testThicknessMm: rawThickness == null || rawThickness === "" ? null : Number(rawThickness),
          testGauge: rawGauge == null || rawGauge === "" ? null : Number(rawGauge),
        });
        const thicknessRow = [...dl.children].find(child => child.querySelector("dt")?.textContent?.trim().toUpperCase() === "THICKNESS COVERAGE");
        if (calculated && thicknessRow) {
          const dd = thicknessRow.querySelector("dd");
          if (dd) dd.textContent = calculated.description;
        }
      }

      card.setAttribute("data-qualification-enriched", "true");
    };

    const clean = () => {
      document.querySelectorAll(".qualification-cards article").forEach(card => {
        card.querySelectorAll("dl > div").forEach(row => {
          const label = row.querySelector("dt")?.textContent?.trim().toUpperCase();
          if (label === "TEST ROUTE" || label === "TEST METHOD" || label === "CERTIFICATE") row.remove();
        });

        card.querySelectorAll("dd, h3").forEach(node => {
          const text = node.textContent?.trim();
          if (!text) return;
          for (const [from, to] of replacements) {
            if (text === from) node.textContent = to;
          }
        });

        void enrichCard(card);
      });

      document.querySelectorAll("option").forEach(option => {
        const text = option.textContent?.trim();
        if (!text) return;
        for (const [from, to] of replacements) {
          if (text === from) option.textContent = to;
        }
      });
    };

    clean();
    const observer = new MutationObserver(clean);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => { cancelled = true; observer.disconnect(); };
  }, []);

  return null;
}
